import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { fetchAZGReserveData, fetchAZSReserveData } from '../services/airtableService';
import { colors } from '../styles/colors';
import { formatNumber, formatCurrency, formatCurrencyFixed } from '../utils/formatters';

const GOLD = '#F2D675';
const SILVER = '#EDEDED';
const GOLD_BORDER = '#C6A84B';
const SILVER_BORDER = '#B8BCC2';

// Count-up animated value (rAF, easeOutCubic). All setState happens inside rAF — no set-state-in-effect.
const AnimatedNumber = ({ value, isVisible, format, duration = 1500 }) => {
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    if (!isVisible || value == null || Number.isNaN(value)) return undefined;
    let raf;
    const start = performance.now();
    const tick = (now) => {
      const t = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - t, 3);
      setDisplay(value * eased);
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [isVisible, value, duration]);
  return <>{format(display)}</>;
};

const LiveReserveData = ({ variant = 'full' }) => {
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(false);
  const [azgData, setAzgData] = useState(null);
  const [azsData, setAzsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const rootRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setIsVisible(true); },
      { threshold: 0, rootMargin: '0px 0px -8% 0px' }
    );
    if (rootRef.current) observer.observe(rootRef.current);
    // Fallback: guarantee reveal + count-up even if the observer doesn't fire
    const fallback = setTimeout(() => setIsVisible(true), 1200);
    return () => { observer.disconnect(); clearTimeout(fallback); };
  }, []);

  // Load AZG and AZS data (API / calculations untouched)
  useEffect(() => {
    const loadData = async () => {
      try {
        const [goldData, silverData] = await Promise.all([
          fetchAZGReserveData(),
          fetchAZSReserveData(),
        ]);
        setAzgData(goldData);
        setAzsData(silverData);
      } catch (error) {
        console.error('Error loading reserve data:', error);
        setAzgData({ totalGoldReserves: 0, compositionBullion: 0, totalTokensInCirculation: 0, reserveValue: 0, totalReserveValue: 0 });
        setAzsData({ totalSilverReserves: 0, compositionBullion: 0, totalTokensInCirculation: 0, reserveValue: 0, totalReserveValue: 0 });
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  // Combined Total Reserve Value = AZG Total Reserve Value + AZS Total Reserve Value (API-supplied, not recalculated)
  const totalReserveValue = (azgData?.totalReserveValue || 0) + (azsData?.totalReserveValue || 0);

  const reveal = (delay) => ({
    transitionDelay: `${delay}ms`,
  });
  const revealCls = isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8';

  const liveCaption = (
    <div className="flex items-center justify-center gap-2 mb-8 sm:mb-10">
      <span className="relative flex h-2 w-2">
        <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-60" style={{ backgroundColor: colors.teal }} />
        <span className="relative inline-flex rounded-full h-2 w-2" style={{ backgroundColor: colors.teal }} />
      </span>
      <span className="text-[11px] uppercase tracking-[0.24em] font-medium" style={{ color: colors.white, opacity: 0.5 }}>
        Updated in real time
      </span>
    </div>
  );

  // ============================ COMPACT (Homepage — reserve-centric) ============================
  if (variant === 'compact') {
    const metrics = [
      {
        label: 'Total Reserve Value',
        testid: 'snapshot-total-reserve-value',
        accent: `${colors.teal}33`,
        node: (
          <>
            <span className="text-2xl sm:text-3xl lg:text-[2.5rem] font-bold leading-none" style={{ color: colors.white }}>
              {loading ? '—' : <AnimatedNumber value={totalReserveValue} isVisible={isVisible} format={formatCurrencyFixed} />}
            </span>
            {!loading && <span className="text-xs font-medium" style={{ color: colors.white, opacity: 0.5 }}>USD</span>}
          </>
        ),
      },
      {
        label: 'Gold Reserve',
        testid: 'snapshot-gold-reserve',
        accent: `${GOLD_BORDER}40`,
        node: (
          <>
            <span className="text-2xl sm:text-3xl lg:text-[2.5rem] font-bold leading-none" style={{ color: GOLD }}>
              {loading ? '—' : <AnimatedNumber value={azgData?.totalGoldReserves} isVisible={isVisible} format={formatNumber} />}
            </span>
            <span className="text-xs font-medium" style={{ color: colors.white, opacity: 0.5 }}>oz</span>
          </>
        ),
      },
      {
        label: 'Silver Reserve',
        testid: 'snapshot-silver-reserve',
        accent: `${SILVER_BORDER}40`,
        node: (
          <>
            <span className="text-2xl sm:text-3xl lg:text-[2.5rem] font-bold leading-none" style={{ color: SILVER }}>
              {loading ? '—' : <AnimatedNumber value={azsData?.totalSilverReserves} isVisible={isVisible} format={formatNumber} />}
            </span>
            <span className="text-xs font-medium" style={{ color: colors.white, opacity: 0.5 }}>oz</span>
          </>
        ),
      },
      {
        label: 'Asset Classes',
        testid: 'snapshot-asset-classes',
        accent: 'rgba(255,255,255,0.08)',
        node: (
          <span className="text-xl sm:text-2xl lg:text-3xl font-bold leading-none" style={{ color: colors.white }}>
            Gold <span style={{ opacity: 0.35 }}>/</span> Silver
          </span>
        ),
      },
    ];

    return (
      <div ref={rootRef} className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className={`flex items-center gap-3 mb-5 justify-center transition-all duration-700 ${revealCls}`}>
          <span className="h-px w-8" style={{ backgroundColor: colors.teal }} />
          <span className="text-[11px] uppercase tracking-[0.28em] font-medium" style={{ color: colors.teal }}>Live Reserve Snapshot</span>
          <span className="h-px w-8" style={{ backgroundColor: colors.teal }} />
        </div>
        {liveCaption}
        {/* Feature card — Total Reserve Value (dominant, full width) */}
        <div
          data-testid="snapshot-total-reserve-value"
          className={`relative rounded-2xl p-7 sm:p-10 text-center overflow-hidden transition-all duration-1000 transform ${revealCls}`}
          style={{ background: 'linear-gradient(180deg, rgba(0,128,128,0.10) 0%, rgba(255,255,255,0.01) 100%)', border: `1px solid ${colors.teal}40`, boxShadow: `0 30px 60px -30px ${colors.teal}40` }}
        >
          <div aria-hidden="true" className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(60% 130% at 50% 0%, rgba(0,128,128,0.14) 0%, rgba(0,0,0,0) 70%)' }} />
          <span className="relative block text-[11px] sm:text-xs uppercase tracking-[0.26em] font-medium mb-4" style={{ color: colors.teal }}>Total Reserve Value</span>
          <div className="relative flex items-baseline justify-center gap-2 flex-wrap">
            <span className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-none tracking-tight" style={{ color: colors.white }}>
              {loading ? '—' : <AnimatedNumber value={totalReserveValue} isVisible={isVisible} format={formatCurrencyFixed} />}
            </span>
            {!loading && <span className="text-sm sm:text-base font-medium" style={{ color: colors.white, opacity: 0.5 }}>USD</span>}
          </div>
        </div>

        {/* Supporting metrics */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 mt-3 sm:mt-4">
          {metrics.slice(1).map((m, i) => (
            <div
              key={m.testid}
              data-testid={m.testid}
              className={`rounded-2xl p-5 sm:p-7 text-center transition-all duration-1000 transform ${revealCls}`}
              style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.045) 0%, rgba(255,255,255,0.01) 100%)', border: `1px solid ${m.accent}`, ...reveal((i + 1) * 120) }}
            >
              <span className="block text-[10px] sm:text-[11px] uppercase tracking-[0.2em] font-medium mb-3" style={{ color: colors.white, opacity: 0.55 }}>{m.label}</span>
              <div className="flex items-baseline justify-center gap-1.5 flex-wrap">{m.node}</div>
            </div>
          ))}
        </div>
        <div className={`flex flex-col sm:flex-row gap-3 justify-center mt-8 transition-all duration-1000 transform ${revealCls}`} style={reveal(500)}>
          <button
            onClick={() => navigate('/reserve-data/azg')}
            data-testid="snapshot-cta-gold"
            className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg text-xs sm:text-sm font-semibold uppercase tracking-[0.14em] transition-all duration-300"
            style={{ backgroundColor: 'transparent', color: colors.white, border: '1px solid rgba(255,255,255,0.2)' }}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = `${GOLD_BORDER}`; e.currentTarget.style.color = GOLD; }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.2)'; e.currentTarget.style.color = colors.white; }}
          >
            View Gold Reserve
            <ArrowRight className="w-4 h-4" />
          </button>
          <button
            onClick={() => navigate('/reserve-data/azs')}
            data-testid="snapshot-cta-silver"
            className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg text-xs sm:text-sm font-semibold uppercase tracking-[0.14em] transition-all duration-300"
            style={{ backgroundColor: 'transparent', color: colors.white, border: '1px solid rgba(255,255,255,0.2)' }}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = `${SILVER_BORDER}`; e.currentTarget.style.color = SILVER; }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.2)'; e.currentTarget.style.color = colors.white; }}
          >
            View Silver Reserve
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  }

  // ============================ FULL (Reserve Architecture) ============================
  const column = ({ logo, name, color, border, totalLabel, totalValue, tokenValue, tokens, ctaLabel, route, testid, delay }) => (
    <div
      data-testid={testid}
      className={`rounded-2xl p-6 sm:p-8 transition-all duration-1000 transform ${revealCls}`}
      style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.01) 100%)', border: `1px solid ${border}50`, ...reveal(delay) }}
    >
      <div className="flex items-center gap-3 mb-6">
        <img src={logo} alt="" aria-hidden="true" className="w-10 h-10 sm:w-12 sm:h-12 object-contain" />
        <h3 className="text-lg sm:text-xl font-bold tracking-tight" style={{ color }}>{name}</h3>
      </div>
      <div className="space-y-4 mb-6">
        <div className="flex justify-between items-baseline border-b pb-3" style={{ borderColor: 'rgba(255,255,255,0.1)' }}>
          <span className="text-sm" style={{ color: colors.white, opacity: 0.6 }}>{totalLabel}</span>
          <div className="flex items-baseline gap-1">
            <span className="text-lg sm:text-xl font-semibold" style={{ color: colors.white }}>
              {loading ? '—' : <AnimatedNumber value={totalValue} isVisible={isVisible} format={formatNumber} />}
            </span>
            <span className="text-xs" style={{ color: colors.white, opacity: 0.4 }}>oz</span>
          </div>
        </div>
        <div className="flex justify-between items-baseline border-b pb-3" style={{ borderColor: 'rgba(255,255,255,0.1)' }}>
          <span className="text-sm" style={{ color: colors.white, opacity: 0.6 }}>Current Token Value</span>
          <div className="flex items-baseline gap-1">
            <span className="text-lg sm:text-xl font-semibold" style={{ color: colors.white }}>
              {loading ? '—' : <AnimatedNumber value={tokenValue} isVisible={isVisible} format={formatCurrency} />}
            </span>
            <span className="text-xs" style={{ color: colors.white, opacity: 0.4 }}>USD</span>
          </div>
        </div>
        <div className="flex justify-between items-baseline">
          <span className="text-sm" style={{ color: colors.white, opacity: 0.6 }}>Tokens in Circulation</span>
          <span className="text-lg sm:text-xl font-semibold" style={{ color: colors.white }}>
            {loading ? '—' : <AnimatedNumber value={tokens} isVisible={isVisible} format={formatNumber} />}
          </span>
        </div>
      </div>
      <button
        onClick={() => navigate(route)}
        data-testid={`${testid}-cta`}
        className="w-full py-3.5 text-sm font-semibold rounded-lg transition-all duration-300"
        style={{ backgroundColor: 'transparent', color, border: `1px solid ${border}` }}
        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = `${border}1f`; }}
        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
      >
        {ctaLabel}
      </button>
    </div>
  );

  return (
    <div ref={rootRef} className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
      {liveCaption}
      {/* Top row — combined reserve metrics */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 mb-6 sm:mb-8">
        {[
          { label: 'Total Reserve Value', testid: 'full-total-reserve-value', color: colors.white, accent: `${colors.teal}33`, node: (loading ? '—' : <AnimatedNumber value={totalReserveValue} isVisible={isVisible} format={formatCurrencyFixed} />), unit: 'USD' },
          { label: 'Gold (AZG)', testid: 'full-gold-reserve', color: GOLD, accent: `${GOLD_BORDER}40`, node: (loading ? '—' : <AnimatedNumber value={azgData?.totalGoldReserves} isVisible={isVisible} format={formatNumber} />), unit: 'oz' },
          { label: 'Silver (AZS)', testid: 'full-silver-reserve', color: SILVER, accent: `${SILVER_BORDER}40`, node: (loading ? '—' : <AnimatedNumber value={azsData?.totalSilverReserves} isVisible={isVisible} format={formatNumber} />), unit: 'oz' },
        ].map((m, i) => (
          <div
            key={m.testid}
            data-testid={m.testid}
            className={`rounded-2xl p-6 sm:p-7 text-center transition-all duration-1000 transform ${revealCls}`}
            style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.045) 0%, rgba(255,255,255,0.01) 100%)', border: `1px solid ${m.accent}`, ...reveal(i * 120) }}
          >
            <span className="block text-[10px] sm:text-[11px] uppercase tracking-[0.2em] font-medium mb-3" style={{ color: colors.white, opacity: 0.55 }}>{m.label}</span>
            <div className="flex items-baseline justify-center gap-1.5 flex-wrap">
              <span className="text-2xl sm:text-3xl lg:text-4xl font-bold leading-none" style={{ color: m.color }}>{m.node}</span>
              {!(loading && m.unit === 'USD') && <span className="text-xs font-medium" style={{ color: colors.white, opacity: 0.5 }}>{m.unit}</span>}
            </div>
          </div>
        ))}
      </div>
      {/* Split detail — AZG | AZS */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
        {column({ logo: '/arizore-logo-3d-azg.png', name: 'AZG Gold Reserve', color: GOLD, border: GOLD_BORDER, totalLabel: 'Total Gold Reserves', totalValue: azgData?.totalGoldReserves, tokenValue: azgData?.currentTokenValue, tokens: azgData?.totalTokensInCirculation, ctaLabel: 'View AZG Reserve', route: '/reserve-data/azg', testid: 'full-azg-column', delay: 200 })}
        {column({ logo: '/arizore-logo-3d-azs.png', name: 'AZS Silver Reserve', color: SILVER, border: SILVER_BORDER, totalLabel: 'Total Silver Reserves', totalValue: azsData?.totalSilverReserves, tokenValue: azsData?.currentTokenValue, tokens: azsData?.totalTokensInCirculation, ctaLabel: 'View AZS Reserve', route: '/reserve-data/azs', testid: 'full-azs-column', delay: 320 })}
      </div>
    </div>
  );
};

export default LiveReserveData;
